/**
 * tokens.ts — a ÚNICA fonte de verdade da identidade visual (Manual de Marca Zentriz v1.0, ago/2026 + docs/adr/).
 *
 * Direção única e obrigatória: este arquivo → scripts/gen-tokens.mjs → src/styles/_tokens.generated.scss (custom properties)
 * e src/brand/theme.ts (MUI). Nenhum outro arquivo do repositório declara cor, tamanho, raio, duração ou easing.
 * Todo par de cor usado em texto/UI está em docs/contrast-matrix.json (recalculada por scripts/verify-contrast.mjs).
 */

// ── Cor (cap. 05; ADR-001/007/011) — semântica de máquina de estados, não estética ────────────────────────
export const color = {
  // superfícies de interface
  tinta: '#0D0916',
  fio: '#201636',
  papel: '#F7F7FA',
  // tinta e papel DA ASSINATURA (só BrandMark — ADR-001)
  markInk: '#150F24',
  markPaper: '#F2F0F7',
  // estados (chapas)
  cura: '#28D2AA',
  construcao: '#4922F7',
  conexao: '#3FA0F0',
  gate: '#E77D13',
  falha: '#EA3E55',
  // variantes de TEXTO com contraste medido (violeta sobre tinta 2,67:1 e verde sobre papel 1,80:1 REPROVAM)
  construcaoOnInk: '#8A71F9',
  curaTextOnPaper: '#0B7A61',
  gateTextOnPaper: '#8A4B0A',
  falhaTextOnPaper: '#9E1228',
  // rampa de neutros (os 6 cinzas do kit, ADR-007; #A49DBE fundido em neutro400)
  neutro100: '#C9C3D8',
  neutro400: '#A79FC0',
  neutro500: '#6E6788',
  neutro600: '#6B62A0',
  neutro800: '#4A4270',
} as const

export type Surface = 'ink' | 'paper'
export type State = 'construcao' | 'cura' | 'conexao' | 'gate' | 'falha'

/** Papéis de cor por superfície — cada par tem linha em docs/contrast-matrix.json. */
export const surface = {
  ink: {
    bg: color.tinta,
    fg: color.papel, // 18,39:1
    fgMuted: color.neutro400, // 7,83:1 — descritor, fólio, etiqueta, placeholder
    action: color.construcaoOnInk, // 5,44:1 — botão primário, link, kicker, foco, régua
    line: color.neutro600, // 3,63:1 — borda com significado (fio #201636 mede 1,15:1: invisível)
    divider: color.neutro800, // 2,16:1 — decorativo, nunca carrega estado
    chip: color.fio, // chapa do inativo
    curaText: color.cura, // 10,20:1
    gateText: color.gate, // 6,87:1
    falhaText: color.falha, // 4,98:1 (ui/nó); texto de erro vai em papel + ícone
    conexaoText: color.conexao, // 7,02:1
    mark: color.markPaper, // assinatura negativa 17,40:1
  },
  paper: {
    bg: color.papel,
    fg: color.tinta,
    fgMuted: color.neutro500, // 4,96:1
    action: color.construcao, // 6,88:1
    line: color.neutro500, // 4,96:1
    divider: color.neutro100, // 1,60:1 — decorativo
    chip: color.neutro100,
    curaText: color.curaTextOnPaper, // 4,95:1
    gateText: color.gateTextOnPaper, // 6,35:1
    falhaText: color.falhaTextOnPaper, // 7,64:1
    conexaoText: color.conexao, // PROIBIDO como texto sobre papel (2,62:1) — azul só em tinta; mantido para o tipo, gate barra
    mark: color.markInk, // assinatura positiva 17,45:1
  },
} as const

/** Chapa de estado e a cor de texto que passa sobre ela (chapa.*), por superfície. */
export const stateChip: Record<State, { bg: string; fg: string; bgOnInk: string; fgOnInk: string; label: { pt: string; en: string } }> = {
  construcao: { bg: color.construcao, fg: color.papel, bgOnInk: color.construcaoOnInk, fgOnInk: color.tinta, label: { pt: 'Em correção', en: 'In progress' } }, // papel/#4922F7 6,88 · tinta/#8A71F9 5,44
  cura: { bg: color.cura, fg: color.tinta, bgOnInk: color.cura, fgOnInk: color.tinta, label: { pt: 'Fechado', en: 'Closed' } }, // 10,20
  conexao: { bg: color.conexao, fg: color.tinta, bgOnInk: color.conexao, fgOnInk: color.tinta, label: { pt: 'Integração', en: 'Integration' } }, // 7,02
  gate: { bg: color.gate, fg: color.tinta, bgOnInk: color.gate, fgOnInk: color.tinta, label: { pt: 'Aguardando', en: 'Awaiting' } }, // 6,87
  falha: { bg: color.falha, fg: color.tinta, bgOnInk: color.falha, fgOnInk: color.tinta, label: { pt: 'Incidente', en: 'Incident' } }, // 4,98 (papel sobre falha = 3,69 reprova)
}

/** Níveis de autonomia (cap. 03): L0–L5. */
export const levels = [
  { level: 0, pt: 'Observa', en: 'Observes' },
  { level: 1, pt: 'Diagnostica', en: 'Diagnoses' },
  { level: 2, pt: 'Propõe', en: 'Proposes' },
  { level: 3, pt: 'Corrige com aprovação', en: 'Fixes with approval' },
  { level: 4, pt: 'Corrige sob política', en: 'Fixes under policy' },
  { level: 5, pt: 'Opera', en: 'Operates' },
] as const
export type Level = (typeof levels)[number]['level']

/** Ordem canônica do pipeline (p.077): o percurso acende os nós nesta ordem e volta ao início (ADR-004). */
export const pipelineOrder: readonly State[] = ['falha', 'construcao', 'gate', 'cura'] as const

// ── Tipografia (cap. 06; ADR-009) — 3 vozes, 7 degraus + kicker, nenhum tamanho intermediário ───────────
export const font = {
  serif: "'Newsreader', 'Newsreader Fallback', Georgia, serif",
  sans: "'Schibsted Grotesk', 'Schibsted Grotesk Fallback', Arial, system-ui, sans-serif",
  mono: "'JetBrains Mono', 'JetBrains Mono Fallback', 'Courier New', ui-monospace, monospace",
} as const

export type Voice = keyof typeof font
export interface Step { voice: Voice; weight: number; size: number; lineHeight: number; tracking: number; uppercase?: boolean }

/** Escala-enum. `size` em px, `tracking` em px (o manual dá em unidades absolutas), lineHeight adimensional. */
export const scale = {
  manifesto: { voice: 'serif', weight: 300, size: 64, lineHeight: 1.04, tracking: -1.8 },
  display: { voice: 'serif', weight: 300, size: 44, lineHeight: 1.1, tracking: -1.2 },
  h1: { voice: 'sans', weight: 600, size: 32, lineHeight: 1.16, tracking: -0.8 },
  h2: { voice: 'sans', weight: 500, size: 22, lineHeight: 1.28, tracking: -0.3 },
  corpo: { voice: 'sans', weight: 400, size: 17, lineHeight: 1.68, tracking: 0 },
  corpoCompacto: { voice: 'sans', weight: 400, size: 15, lineHeight: 1.6, tracking: 0 }, // 15/24 da anatomia (p.083: lede e campo)
  kicker: { voice: 'mono', weight: 500, size: 11, lineHeight: 1.5, tracking: 0.88, uppercase: true }, // 11/mono, +8% de entreletra (p.081)
  etiqueta: { voice: 'mono', weight: 500, size: 10, lineHeight: 1.5, tracking: 1.8, uppercase: true }, // p.063
  dado: { voice: 'mono', weight: 400, size: 13, lineHeight: 1.5, tracking: 0.4 },
} as const satisfies Record<string, Step>

export type ScaleStep = keyof typeof scale
/** Par de vozes por peça: 'editorial' (serifa Light + Schibsted Regular) ou 'tela' (Schibsted SemiBold + Regular) — nunca os dois (máx. 2 pesos + mono). */
export type Variant = 'editorial' | 'tela'

/** Medida: 65 caracteres é o máximo (p.093); o campo usa max-width em ch. */
export const measure = { maxCh: 65 } as const

// ── Forma (cap. 07; ADR-002) ─────────────────────────────────────────────────────────────────────────────
export const radius = {
  /** O raio é uma RAZÃO: 10% do menor lado do elemento (R = 20 numa caixa de 200 no Símbolo v3.1). */
  ratio: 0.1,
  /** Teto em moldura plena (cartão, chapa, contêiner de imagem) = 10% de 280px, o menor cartão do sistema. */
  framePx: 28,
} as const

/** Assinatura (cap. 04; ADR-003): respiro = 1 nó = 2u = 20% da largura do SÍMBOLO; header fixo em 120px. */
export const mark = { clearRatioOfSymbol: 0.2, minWidthPx: 120, headerWidthPx: 120 } as const

// ── Movimento (cap. 07; ADR-004/006) ─────────────────────────────────────────────────────────────────────
export const motion = {
  /** Duas leituras do MESMO arco do canto do símbolo (κ = 0,55228). */
  easeArcoOut: 'cubic-bezier(0, 0.5523, 0.4477, 1)',
  easeArcoIn: 'cubic-bezier(0.5523, 0, 1, 0.4477)',
  /** Cor de estado NUNCA interpola: troca discreta. */
  step: 'step-end',
  durFastMs: 120,
  durBaseMs: 320,
  /** Cadência de nó (1 nó a cada 6 unidades de caminho → 420 ms; 4 nós = 1.680 ms). */
  nodeMs: 420,
} as const

// ── Grade (cap. 09; ADR-014) — razões da peça de referência 1920 (12 × 118, gutter 24, margem 120) ─────
export const grid = {
  columns: 12,
  marginRatio: 120 / 1920, // 6,25%
  gutterRatio: 24 / 1920, // 1,25%
  marginMinPx: 24,
  gutterMinPx: 12,
  trailColumns: 3, // trilho à esquerda: kicker, título, régua
  fieldColumnsMax: 3, // campo: no máximo 3 colunas de texto
  collapseBelowPx: 768, // 1 coluna abaixo disto (trilho acima do campo)
  verticalPlacement: 0.4, // bloco assenta a 40% do vão residual — nunca centralizado
  /** Intervalos verticais do cap. 09 (18 · 24 · 22 · 40) + base 8 para o MUI. */
  gaps: { kickerTitle: 18, titleRule: 24, ruleLede: 22, ledeField: 40 },
} as const

/** Régua do trilho (p.083): 56 × 2, separa o título do que vem depois. */
export const rule = { widthPx: 56, heightPx: 2 } as const

/** Espaçamento (múltiplos de 8, mais os intervalos da anatomia). */
export const space = [0, 4, 8, 12, 16, 18, 22, 24, 32, 40, 48, 64, 96, 128] as const

/** Breakpoints: Home 1440 é a referência; 1024 e 390 são as derivações do ADR-014. */
export const breakpoints = { mobile: 390, tablet: 768, desktop: 1024, reference: 1440 } as const
